import React, { useState, useEffect } from 'react';
import { BaseEditModal } from 'components/edit-modals/base-edit-modal';
import { CopyLinkIcon } from 'assets';
import { useToast, useShareReport } from 'hooks';

interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId?: string;
  assessmentId?: string;
}

export const ShareReportModal: React.FC<ShareReportModalProps> = ({ isOpen, onClose, reportId, assessmentId }) => {
  const [shareEmails, setShareEmails] = useState('');
  const { openToast } = useToast();
  const { shareReport, isSharing, isSuccess } = useShareReport();

  // Close modal on successful share
  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  const handleShareSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareEmails.trim()) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Please enter at least one email address',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    if (!reportId) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Report ID is missing',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    // Process emails - split by comma and trim
    const emailList = shareEmails.split(',').map(email => email.trim()).filter(email => email);

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Please enter valid email addresses',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    // Share report with each email
    emailList.forEach((email) => {
      shareReport({ reportId, email });
    });
  };

  const handleCopyLink = () => {
    if (!assessmentId || !reportId) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Report information is missing',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    const baseUrl = window.location.origin;
    const reportLink = `${baseUrl}/shared-report/${assessmentId}/${reportId}`;
    
    navigator.clipboard.writeText(reportLink).then(() => {
      openToast({
        show: true,
        heading: 'Success',
        text: 'Link copied to clipboard!',
        type: 'info',
        timeOut: 5000,
      });
    }).catch(() => {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Failed to copy link',
        type: 'error',
        timeOut: 5000,
      });
    });
  };

  const handleClose = () => {
    setShareEmails('');
    onClose();
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Share Report"
      onSubmit={handleCopyLink}
      saveButtonText="Copy Link"
      cancelButtonText=""
      hasIcon={true}
      icon={<CopyLinkIcon className="w-5 h-5" />}
      saveButtonClassName="flex items-center gap-2 !bg-white !text-[#012169] !border !border-[#012169] hover:bg-[#F9FAFB] hover:text-[#012169]"
    >
      <div className="flex gap-4 w-[90%] mx-auto my-5">
        <input
          type="text"
          placeholder="Share via email (separate emails with comma)"
          value={shareEmails}
          onChange={(e) => setShareEmails(e.target.value)}
          className="w-full px-4 py-3 border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#012169] focus:border-transparent text-sm text-[#667085] placeholder:text-[#98A2B3]"
        />
        <button
          type="button"
          onClick={handleShareSubmit}
          disabled={!shareEmails.trim() || isSharing}
          className={`${!shareEmails.trim() || isSharing ? 'opacity-50 cursor-not-allowed' : ''} flex items-center gap-2 px-6 py-2.5 border border-[#012169] !bg-[#012169] rounded-lg hover:bg-[#012169] text-[#FFFFFF] hover:text-white transition-colors text-sm font-medium`}
        >
          {isSharing ? 'Sharing...' : 'Share'}
        </button>
      </div>
    </BaseEditModal>
  );
};
